import { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, canViewAllVitals } from '../../utils/auth';
import {
  getPatients,
  getVitals,
  getPatientVitals,
  addEditRequest,
  addLog,
  updateVital,
  markPatientWhatsAppShared,
  addWhatsAppShareRequest,
  getWhatsAppShareRequests,
  updateWhatsAppShareRequestStatus,
  getUsers,
} from '../../utils/storage';
import { getDeviceInfo, getIPAddress } from '../../utils/device';
import type { Patient, VitalReading, WhatsAppShareRequest } from '../../types';
import DashboardLayout from '../../components/feature/DashboardLayout';

const getVitalIcon = (type: string) => {
  const icons: Record<string, string> = {
    bp: 'ri-heart-pulse-line',
    heart_rate: 'ri-heart-line',
    spo2: 'ri-lungs-line',
    temperature: 'ri-temp-hot-line',
    rbs: 'ri-drop-line',
    cholesterol: 'ri-test-tube-line',
    height_weight: 'ri-ruler-line',
    blood_grouping: 'ri-contrast-drop-2-line',
    colour_blindness: 'ri-eye-line',
    visual_acuity: 'ri-eye-2-line',
    ecg: 'ri-pulse-line',
    body_fat: 'ri-body-scan-line',
  };
  return icons[type] || 'ri-file-line';
};

const getVitalLabel = (type: string) => {
  const labels: Record<string, string> = {
    bp: 'Blood Pressure',
    heart_rate: 'Heart Rate',
    spo2: 'SpO2',
    temperature: 'Temperature',
    rbs: 'Random Blood Sugar',
    cholesterol: 'Cholesterol',
    height_weight: 'Height & Weight',
    blood_grouping: 'Blood Grouping',
    colour_blindness: 'Colour Blindness',
    visual_acuity: 'Visual Acuity',
    ecg: 'ECG',
    body_fat: 'Body Fat',
  };
  return labels[type] || type;
};

const getVitalColor = (type: string) => {
  const colors: Record<string, string> = {
    bp: 'text-red-500',
    heart_rate: 'text-red-500',
    spo2: 'text-teal-500',
    temperature: 'text-orange-500',
    rbs: 'text-red-600',
    cholesterol: 'text-green-500',
    height_weight: 'text-indigo-500',
    blood_grouping: 'text-rose-600',
    colour_blindness: 'text-purple-600',
    visual_acuity: 'text-blue-600',
    ecg: 'text-pink-600',
    body_fat: 'text-amber-600',
  };
  return colors[type] || 'text-teal-600';
};

const getVitalBg = (type: string) => {
  const bgs: Record<string, string> = {
    bp: 'bg-red-50',
    heart_rate: 'bg-red-50',
    spo2: 'bg-teal-50',
    temperature: 'bg-orange-50',
    rbs: 'bg-rose-50',
    cholesterol: 'bg-green-50',
    height_weight: 'bg-indigo-50',
    blood_grouping: 'bg-rose-50',
    colour_blindness: 'bg-purple-50',
    visual_acuity: 'bg-blue-50',
    ecg: 'bg-pink-50',
    body_fat: 'bg-amber-50',
  };
  return bgs[type] || 'bg-teal-50';
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const vitalTypes = [
  'all',
  'bp',
  'heart_rate',
  'spo2',
  'temperature',
  'rbs',
  'cholesterol',
  'height_weight',
  'blood_grouping',
  'colour_blindness',
  'visual_acuity',
  'ecg',
  'body_fat',
];

// ── Excel Export ── one row per patient, each vital type as its own column ──
const exportToExcel = (vitals: VitalReading[], patientMap: Record<string, Patient>) => {
  // Group vitals by patient then by type
  const byPatient: Record<string, Record<string, VitalReading[]>> = {};
  vitals.forEach((v) => {
    if (!byPatient[v.patientId]) byPatient[v.patientId] = {};
    if (!byPatient[v.patientId][v.type]) byPatient[v.patientId][v.type] = [];
    byPatient[v.patientId][v.type].push(v);
  });

  const headers = [
    'Patient ID',
    'Patient Name',
    'College',
    'Department',
    'Contact',
    'Blood Group',
    'Blood Pressure (mmHg)',
    'Heart Rate (bpm)',
    'SpO2 (%)',
    'Temperature (°F)',
    'Random Blood Sugar (mg/dL)',
    'Cholesterol (mg/dL)',
    'Height & Weight',
    'BMI',
    'BMI Category',
    'Last Recorded At',
  ];

  const patientIds = [...new Set(vitals.map((v) => v.patientId))];

  const rows = patientIds.map((pid) => {
    const p = patientMap[pid];
    const pv = byPatient[pid] || {};

    const getLatest = (type: VitalReading['type']): string => {
      const entries = pv[type];
      if (!entries || entries.length === 0) return '';
      return [...entries]
        .sort(
          (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
        )[0].value;
    };

    // Calculate BMI from height_weight
    const heightWeightValue = getLatest('height_weight');
    let bmiValue = '';
    let bmiCategory = '';
    
    if (heightWeightValue) {
      const match = heightWeightValue.match(/(\d+(?:\.\d+)?)\s*cm\s*\/\s*(\d+(?:\.\d+)?)\s*kg/i);
      if (match) {
        const heightCm = parseFloat(match[1]);
        const weightKg = parseFloat(match[2]);
        if (heightCm > 0 && weightKg > 0) {
          const heightM = heightCm / 100;
          const bmi = weightKg / (heightM * heightM);
          bmiValue = bmi.toFixed(1);
          
          if (bmi < 18.5) {
            bmiCategory = 'Underweight';
          } else if (bmi >= 18.5 && bmi < 25) {
            bmiCategory = 'Normal';
          } else if (bmi >= 25 && bmi < 30) {
            bmiCategory = 'Overweight';
          } else {
            bmiCategory = 'Obese';
          }
        }
      }
    }

    const allEntries = Object.values(pv).flat();
    const lastRecordedAt =
      allEntries.length > 0
        ? formatDate(
            [...allEntries]
              .sort(
                (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
              )[0].recordedAt,
          )
        : '';

    return [
      pid,
      p?.name || '',
      p?.college || '',
      p?.department || '',
      p?.contactNumber || '',
      p?.bloodGroup || '',
      getLatest('bp'),
      getLatest('heart_rate'),
      getLatest('spo2'),
      getLatest('temperature'),
      getLatest('rbs'),
      getLatest('cholesterol'),
      heightWeightValue,
      bmiValue,
      bmiCategory,
      lastRecordedAt,
    ];
  });

  const csvContent = [headers, ...rows]
    .map((row) =>
      row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','),
    )
    .join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `vitals_export_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// ── Patient Vitals Detail Panel ──
interface PatientVitalsDetailProps {
  patient: Patient;
  patientVitals: VitalReading[];
  onClose: () => void;
  onEditRequest: (vital: VitalReading) => void;
  canRequestEdit: (vital: VitalReading) => boolean;
  onShareWhatsApp?: () => void;
}

function PatientVitalsDetail({
  patient,
  patientVitals,
  onClose,
  onEditRequest,
  canRequestEdit,
  onShareWhatsApp,
}: PatientVitalsDetailProps) {
  const [filterType, setFilterType] = useState('all');

  const vitals = useMemo(
    () =>
      [...patientVitals].sort(
        (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime(),
      ),
    [patientVitals],
  );

  const vitalCounts: Record<string, number> = {};
  vitals.forEach((v) => {
    vitalCounts[v.type] = (vitalCounts[v.type] || 0) + 1;
  });

  const filteredVitals =
    filterType === 'all' ? vitals : vitals.filter((v) => v.type === filterType);

  // Calculate BMI for display
  const heightWeightVital = vitals.find(v => v.type === 'height_weight');
  let bmiDisplay: { bmi: number; category: string; color: string } | null = null;

  if (heightWeightVital) {
    const match = heightWeightVital.value.match(/(\d+(?:\.\d+)?)\s*cm\s*\/\s*(\d+(?:\.\d+)?)\s*kg/i);
    if (match) {
      const heightCm = parseFloat(match[1]);
      const weightKg = parseFloat(match[2]);
      if (heightCm > 0 && weightKg > 0) {
        const heightM = heightCm / 100;
        const bmi = weightKg / (heightM * heightM);
        let category = '';
        let color = '';
        if (bmi < 18.5) { category = 'Underweight'; color = 'text-blue-600'; }
        else if (bmi < 25) { category = 'Normal'; color = 'text-green-600'; }
        else if (bmi < 30) { category = 'Overweight'; color = 'text-orange-600'; }
        else { category = 'Obese'; color = 'text-red-600'; }
        bmiDisplay = { bmi: parseFloat(bmi.toFixed(1)), category, color };
      }
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4 py-6">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-gray-100">
          <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg flex-shrink-0">
            {patient.name.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-bold text-gray-800">{patient.name}</h2>
            <p className="text-sm text-gray-500">
              ID: {patient.id} &bull; {patient.contactNumber} &bull; {patient.college}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {onShareWhatsApp && (
              <button
                onClick={onShareWhatsApp}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
              >
                <i className="ri-whatsapp-line text-sm"></i>
                Share
              </button>
            )}
            <span className="px-3 py-1 bg-teal-500 text-white text-xs font-medium rounded-full border border-teal-200">
              {patient.bloodGroup}
            </span>
            <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">
              {patient.gender}
            </span>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors cursor-pointer text-gray-500 ml-2"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>

        {/* Patient info grid */}
        <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/50">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs text-gray-400">Age</p>
              <p className="font-semibold text-gray-700">{patient.age} years</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Department</p>
              <p className="font-semibold text-gray-700 truncate">{patient.department}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Year / Designation</p>
              <p className="font-semibold text-gray-700">{patient.yearDesignation}</p>
            </div>
            <div>
              <p className="text-xs text-gray-400">Total Vitals</p>
              <p className="font-semibold text-gray-700">{vitals.length} records</p>
            </div>
          </div>
          {bmiDisplay && (
            <div className="mt-3 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-600 mb-0.5">Body Mass Index (BMI)</p>
                  <p className={`text-xl font-bold ${bmiDisplay.color}`}>{bmiDisplay.bmi}</p>
                </div>
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${bmiDisplay.color} bg-white border-2`}>
                  {bmiDisplay.category}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Filter chips */}
        {vitals.length > 0 && (
          <div className="px-6 py-3 border-b border-gray-100 flex flex-wrap gap-2">
            {vitalTypes.map((type) => {
              const count = type === 'all' ? vitals.length : vitalCounts[type] || 0;
              if (type !== 'all' && count === 0) return null;
              return (
                <button
                  key={type}
                  onClick={() => setFilterType(type)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterType === type ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {type === 'all' ? 'All' : getVitalLabel(type)} ({count})
                </button>
              );
            })}
          </div>
        )}

        {/* Vitals list */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {filteredVitals.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
                <i className="ri-file-list-3-line text-5xl text-gray-200"></i>
              </div>
              <p className="text-sm font-medium text-gray-500">No vitals recorded yet</p>
              <p className="text-xs text-gray-400 mt-1">Vitals will appear here once recorded</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredVitals.map((vital) => (
                <div key={vital.id} className="border border-gray-100 rounded-xl p-4 hover:shadow-sm transition-shadow bg-gray-50/40">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <div className={`w-10 h-10 ${getVitalBg(vital.type)} rounded-xl flex items-center justify-center flex-shrink-0`}>
                        <i className={`${getVitalIcon(vital.type)} text-lg ${getVitalColor(vital.type)}`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-semibold text-gray-800">{getVitalLabel(vital.type)}</h4>
                          {vital.edited && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                              <i className="ri-edit-line text-xs"></i>Edited
                            </span>
                          )}
                          {vital.editRequested && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full">
                              <i className="ri-time-line text-xs"></i>Edit Pending
                            </span>
                          )}
                        </div>
                        <p className={`text-xl font-bold mt-0.5 ${getVitalColor(vital.type)}`}>
                          {vital.value}{' '}
                          <span className="text-sm font-normal text-gray-500">{vital.unit}</span>
                        </p>
                        {vital.edited && vital.previousValue && (
                          <p className="text-xs text-amber-600 mt-1">
                            Previous: {vital.previousValue} {vital.unit}
                            {vital.editedAt && ` · Edited on ${formatDate(vital.editedAt)}`}
                          </p>
                        )}
                        <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-400">
                          <span className="flex items-center gap-1"><i className="ri-user-line"></i>{vital.recordedBy}</span>
                          <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{formatDate(vital.recordedAt)}</span>
                        </div>
                      </div>
                    </div>
                    {canRequestEdit(vital) && (
                      <button
                        onClick={() => onEditRequest(vital)}
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
                      >
                        <i className="ri-edit-box-line"></i>Request Edit
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── WhatsApp Report Sharing ──
const generateWhatsAppReport = (patient: Patient, vitals: VitalReading[]) => {
  // Group vitals by type and get latest value for each
  const vitalsByType: Record<string, VitalReading> = {};
  vitals.forEach((v) => {
    if (!vitalsByType[v.type] || new Date(v.recordedAt) > new Date(vitalsByType[v.type].recordedAt)) {
      vitalsByType[v.type] = v;
    }
  });

  // Calculate BMI if height_weight exists
  let bmiText = '';
  if (vitalsByType['height_weight']) {
    const match = vitalsByType['height_weight'].value.match(/(\d+(?:\.\d+)?)\s*cm\s*\/\s*(\d+(?:\.\d+)?)\s*kg/i);
    if (match) {
      const heightCm = parseFloat(match[1]);
      const weightKg = parseFloat(match[2]);
      if (heightCm > 0 && weightKg > 0) {
        const heightM = heightCm / 100;
        const bmi = weightKg / (heightM * heightM);
        let category = '';
        
        if (bmi < 18.5) {
          category = 'Underweight';
        } else if (bmi >= 18.5 && bmi < 25) {
          category = 'Normal';
        } else if (bmi >= 25 && bmi < 30) {
          category = 'Overweight';
        } else {
          category = 'Obese';
        }
        
        bmiText = `📊 BMI: ${bmi.toFixed(1)} (${category})\n`;
      }
    }
  }

  // Build report with centered headers
  let report = ``;
  report += `         *SONA COLLEGE OF*\n`;
  report += `           *TECHNOLOGY*\n`;
  report += `         *DEPARTMENT OF*\n`;
  report += `   *BIO MEDICAL ENGINEERING*\n`;
  report += `          *MEDSPARKZ\'26*\n`;
  report += `          *MEDICAL CAMP*\n\n`;
  
  report += `━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  report += `*Patient Details:*\n`;
  report += `Name: ${patient.name}\n`;
  report += `ID: ${patient.id}\n`;
  report += `Age: ${patient.age} years\n`;
  report += `Gender: ${patient.gender}\n\n`;
  
  report += `*Vital Signs:*\n\n`;
  
  // Temperature
  if (vitalsByType['temperature']) {
    report += `🌡️ Temperature: ${vitalsByType['temperature'].value} ${vitalsByType['temperature'].unit}\n`;
  }
  
  // Height & Weight
  if (vitalsByType['height_weight']) {
    report += `📏 Height & Weight: ${vitalsByType['height_weight'].value}\n`;
  }
  
  // BMI
  if (bmiText) {
    report += bmiText;
  }
  
  // Heart Rate
  if (vitalsByType['heart_rate']) {
    report += `❤️ Heart Rate: ${vitalsByType['heart_rate'].value} ${vitalsByType['heart_rate'].unit}\n`;
  }
  
  // Blood Pressure
  if (vitalsByType['bp']) {
    report += `💉 Blood Pressure: ${vitalsByType['bp'].value} ${vitalsByType['bp'].unit}\n`;
  }
  
  // Random Blood Sugar
  if (vitalsByType['rbs']) {
    report += `🩸 Random Blood Glucose: ${vitalsByType['rbs'].value} ${vitalsByType['rbs'].unit}\n`;
  }
  
  // SpO2
  if (vitalsByType['spo2']) {
    report += `🫁 SpO2: ${vitalsByType['spo2'].value} ${vitalsByType['spo2'].unit}\n`;
  }
  
  report += `\n━━━━━━━━━━━━━━━━━━━━\n\n`;
  
  report += `*Note:* These findings are screening results only and do not constitute a definitive medical diagnosis.\n`;
  
  report += `\n━━━━━━━━━━━━━━━━━━━━\n`;
  report += `_Powered by Harishwar Solutions_`;
  
  return report;
};

const shareViaWhatsApp = (patient: Patient, vitals: VitalReading[], onShared?: () => void) => {
  const report = generateWhatsAppReport(patient, vitals);
  const encodedMessage = encodeURIComponent(report);
  
  let whatsappUrl = '';
  if (patient.whatsappNumber) {
    // Remove any non-digit characters from phone number
    const cleanNumber = patient.whatsappNumber.replace(/\D/g, '');
    whatsappUrl = `https://wa.me/${cleanNumber}?text=${encodedMessage}`;
  } else {
    whatsappUrl = `https://wa.me/?text=${encodedMessage}`;
  }
  
  window.open(whatsappUrl, '_blank');
  
  // Mark as shared
  if (onShared) {
    onShared();
  }
};

// ── Main Page ──
export default function ViewVitals() {
  const navigate = useNavigate();
  const user = getUser();
  const isAdminOrMaster = user?.role === 'admin' || user?.role === 'master_admin';

  const [activeTab, setActiveTab] = useState<'search' | 'all' | 'share_requests'>(
    isAdminOrMaster ? 'all' : 'search',
  );

  // Data state
  const [allPatients, setAllPatients] = useState<Patient[]>([]);
  const [allVitals, setAllVitals] = useState<VitalReading[]>([]);
  const [dataLoading, setDataLoading] = useState(true);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [searchResults, setSearchResults] = useState<Patient[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [patientVitals, setPatientVitals] = useState<VitalReading[]>([]);
  const [filterType, setFilterType] = useState<string>('all');

  // All-entries state
  const [allFilterType, setAllFilterType] = useState<string>('all');
  const [allSearch, setAllSearch] = useState('');
  const [expandedPatientId, setExpandedPatientId] = useState<string | null>(null);

  // Edit modal
  const [editModal, setEditModal] = useState<{ vital: VitalReading | null; show: boolean }>({ vital: null, show: false });
  const [editReason, setEditReason] = useState('');
  const [editNewValue, setEditNewValue] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // WhatsApp share requests
  const [shareRequests, setShareRequests] = useState<WhatsAppShareRequest[]>([]);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [requestReason, setRequestReason] = useState('');
  const [requestPatientId, setRequestPatientId] = useState<string | null>(null);

  useEffect(() => {
    if (!user || !canViewAllVitals(user)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  // Load all data on mount
  const loadAllData = useCallback(async () => {
    setDataLoading(true);
    const [patients, vitals] = await Promise.all([getPatients(), getVitals()]);
    setAllPatients(patients);
    setAllVitals(vitals.sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()));
    setDataLoading(false);
  }, []);

  useEffect(() => {
    loadAllData();
  }, [loadAllData]);

  useEffect(() => {
    if (isAdminOrMaster) {
      loadShareRequests();
    }
  }, [isAdminOrMaster, activeTab]);

  const loadShareRequests = async () => {
    const requests = await getWhatsAppShareRequests();
    setShareRequests(requests.filter(r => r.status === 'pending'));
  };

  // ── Search tab ──
  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.trim().length > 0) {
      const results = allPatients.filter(
        (p) =>
          p.id.toLowerCase().includes(value.toLowerCase()) ||
          p.name.toLowerCase().includes(value.toLowerCase()) ||
          p.contactNumber.includes(value),
      );
      setSearchResults(results);
      setShowResults(true);
    } else {
      setSearchResults([]);
      setShowResults(false);
    }
  };

  const selectPatient = async (patient: Patient) => {
    setSelectedPatient(patient);
    setSearchTerm(`${patient.name} (${patient.id})`);
    setShowResults(false);
    setFilterType('all');
    const vitals = await getPatientVitals(patient.id);
    setPatientVitals(vitals.sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()));
  };

  const refreshPatientVitals = async () => {
    if (selectedPatient) {
      const vitals = await getPatientVitals(selectedPatient.id);
      setPatientVitals(vitals.sort((a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()));
    }
  };

  // ── Derived data ──
  const patientMap = useMemo(() => {
    const map: Record<string, Patient> = {};
    allPatients.forEach((p) => { map[p.id] = p; });
    return map;
  }, [allPatients]);

  const allVitalCounts: Record<string, number> = {};
  allVitals.forEach((v) => {
    allVitalCounts[v.type] = (allVitalCounts[v.type] || 0) + 1;
  });

  const filteredPatients = useMemo(() => {
    if (!allSearch.trim()) return allPatients;
    const q = allSearch.toLowerCase();
    return allPatients.filter(
      (p) =>
        p.id.toLowerCase().includes(q) ||
        p.name.toLowerCase().includes(q) ||
        p.contactNumber.includes(q),
    );
  }, [allPatients, allSearch]);

  const vitalsByPatient = useMemo(() => {
    const map: Record<string, VitalReading[]> = {};
    allVitals.forEach((v) => {
      if (!map[v.patientId]) map[v.patientId] = [];
      map[v.patientId].push(v);
    });
    return map;
  }, [allVitals]);

  const filteredAllVitals = useMemo(() => {
    let result = allVitals;
    if (allFilterType !== 'all') result = result.filter((v) => v.type === allFilterType);
    if (allSearch.trim()) {
      const q = allSearch.toLowerCase();
      result = result.filter((v) => {
        const p = patientMap[v.patientId];
        return (
          v.patientId.toLowerCase().includes(q) ||
          p?.name?.toLowerCase().includes(q) ||
          p?.contactNumber?.includes(q)
        );
      });
    }
    return result;
  }, [allVitals, allFilterType, allSearch, patientMap]);

  // ── WhatsApp Share Logic ──
  const handleWhatsAppShare = async (patient: Patient, vitals: VitalReading[]) => {
    if (!user) return;

    if (patient.whatsappShared && !isAdminOrMaster) {
      setRequestPatientId(patient.id);
      setShowRequestModal(true);
      return;
    }

    shareViaWhatsApp(patient, vitals, async () => {
      await markPatientWhatsAppShared(patient.id);

      const ipAddress = await getIPAddress();
      const { browser, device } = getDeviceInfo();
      await addLog({
        id: Date.now().toString(),
        userId: user.id,
        username: user.username,
        action: 'WhatsApp Share',
        details: `Shared vitals report via WhatsApp for patient ${patient.name} (${patient.id})`,
        ipAddress,
        deviceName: device,
        browser,
        timestamp: new Date().toISOString(),
      });

      setSuccessMsg('Report shared via WhatsApp successfully!');
      setTimeout(() => setSuccessMsg(''), 3000);

      // Refresh patient list
      await loadAllData();
      if (selectedPatient?.id === patient.id) {
        const updated = allPatients.find(p => p.id === patient.id);
        if (updated) setSelectedPatient({ ...updated, whatsappShared: true });
      }
    });
  };

  const handleRequestReShare = async () => {
    if (!user || !requestPatientId || !requestReason.trim()) return;

    const patient = allPatients.find(p => p.id === requestPatientId);
    if (!patient) return;

    const users = await getUsers();
    const currentUser = users.find(u => u.id === user.id);
    const displayName = currentUser?.displayName || user.username;

    const requestId = `WSR${Date.now()}`;
    await addWhatsAppShareRequest({
      id: requestId,
      patientId: patient.id,
      patientName: patient.name,
      requestedBy: user.username,
      requestedByName: displayName,
      requestedAt: new Date().toISOString(),
      status: 'pending',
    });

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    await addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'WhatsApp Re-share Request',
      details: `Requested re-share permission for patient ${patient.name} (${patient.id}). Reason: ${requestReason}`,
      ipAddress,
      deviceName: device,
      browser,
      timestamp: new Date().toISOString(),
    });

    setShowRequestModal(false);
    setRequestReason('');
    setRequestPatientId(null);
    setSuccessMsg('Re-share request submitted! Waiting for admin approval.');
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleApproveShareRequest = async (request: WhatsAppShareRequest) => {
    if (!user) return;

    await updateWhatsAppShareRequestStatus(request.id, 'approved', user.username);

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    await addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'WhatsApp Re-share Approved',
      details: `Approved re-share request for patient ${request.patientName} (${request.patientId}) requested by ${request.requestedByName}`,
      ipAddress,
      deviceName: device,
      browser,
      timestamp: new Date().toISOString(),
    });

    const patient = allPatients.find(p => p.id === request.patientId);
    if (patient) {
      const pVitals = await getPatientVitals(patient.id);
      shareViaWhatsApp(patient, pVitals, async () => {
        await markPatientWhatsAppShared(patient.id);
      });
    }

    await loadShareRequests();
    setSuccessMsg(`Re-share request approved! WhatsApp share enabled for ${request.patientName}.`);
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const handleRejectShareRequest = async (request: WhatsAppShareRequest) => {
    if (!user) return;

    await updateWhatsAppShareRequestStatus(request.id, 'rejected', user.username);

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    await addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'WhatsApp Re-share Rejected',
      details: `Rejected re-share request for patient ${request.patientName} (${request.patientId}) requested by ${request.requestedByName}`,
      ipAddress,
      deviceName: device,
      browser,
      timestamp: new Date().toISOString(),
    });

    await loadShareRequests();
    setSuccessMsg(`Re-share request rejected for ${request.patientName}.`);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  // ── Edit request ──
  const openEditRequest = (vital: VitalReading) => {
    setEditModal({ vital, show: true });
    setEditNewValue(vital.value);
    setEditReason('');
  };

  const handleSubmitEditRequest = async () => {
    if (!editModal.vital || !editReason.trim() || !editNewValue.trim() || !user) return;

    const patientForEdit = selectedPatient || patientMap[editModal.vital.patientId];
    if (!patientForEdit) return;

    const requestId = `ER${Date.now()}`;

    await addEditRequest({
      id: requestId,
      patientId: patientForEdit.id,
      patientName: patientForEdit.name,
      vitalId: editModal.vital.id,
      vitalType: editModal.vital.type,
      currentValue: editModal.vital.value,
      newValue: editNewValue,
      requestedBy: user.username,
      requestedAt: new Date().toISOString(),
      reason: editReason,
      status: 'pending',
    });

    await updateVital(editModal.vital.id, { editRequested: true, editRequestId: requestId });

    const { browser, device } = getDeviceInfo();
    const ipAddress = await getIPAddress();
    await addLog({
      id: Date.now().toString(),
      userId: user.id,
      username: user.username,
      action: 'Edit Request',
      details: `Requested edit for ${getVitalLabel(editModal.vital.type)} of patient ${patientForEdit.name} (${patientForEdit.id}). Current: ${editModal.vital.value}, New: ${editNewValue}. Reason: ${editReason}`,
      ipAddress,
      deviceName: device,
      browser,
      timestamp: new Date().toISOString(),
    });

    setEditModal({ vital: null, show: false });
    setEditReason('');
    setEditNewValue('');
    setSuccessMsg('Edit request submitted successfully! An admin will review it.');
    await refreshPatientVitals();
    setTimeout(() => setSuccessMsg(''), 4000);
  };

  const canRequestEdit = (vital: VitalReading): boolean => {
    if (!user) return false;
    if (user.role === 'view_only') return false;
    if (vital.editRequested) return false;
    return true;
  };

  const filteredVitals = filterType === 'all' ? patientVitals : patientVitals.filter((v) => v.type === filterType);
  const vitalCounts: Record<string, number> = {};
  patientVitals.forEach((v) => { vitalCounts[v.type] = (vitalCounts[v.type] || 0) + 1; });

  const expandedPatient = expandedPatientId ? patientMap[expandedPatientId] : null;
  const expandedPatientVitals = expandedPatientId ? (vitalsByPatient[expandedPatientId] || []) : [];

  const selectedPatientBMI = useMemo(() => {
    if (!selectedPatient) return null;
    const hwVital = patientVitals.find(v => v.type === 'height_weight');
    if (!hwVital) return null;
    const match = hwVital.value.match(/(\d+(?:\.\d+)?)\s*cm\s*\/\s*(\d+(?:\.\d+)?)\s*kg/i);
    if (!match) return null;
    const heightCm = parseFloat(match[1]);
    const weightKg = parseFloat(match[2]);
    if (heightCm <= 0 || weightKg <= 0) return null;
    const heightM = heightCm / 100;
    const bmi = weightKg / (heightM * heightM);
    let category = '', color = '';
    if (bmi < 18.5) { category = 'Underweight'; color = 'text-blue-600'; }
    else if (bmi < 25) { category = 'Normal'; color = 'text-green-600'; }
    else if (bmi < 30) { category = 'Overweight'; color = 'text-orange-600'; }
    else { category = 'Obese'; color = 'text-red-600'; }
    return { bmi: parseFloat(bmi.toFixed(1)), category, color };
  }, [selectedPatient, patientVitals]);

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-800 cursor-pointer whitespace-nowrap mb-4 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-teal-100 rounded-xl flex items-center justify-center">
                <i className="ri-eye-line text-teal-600 text-xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">View Vitals</h1>
                <p className="text-sm text-gray-500">
                  {isAdminOrMaster
                    ? 'View all patient vitals or search by patient'
                    : 'Search a patient to view their complete vitals history'}
                </p>
              </div>
            </div>
            {isAdminOrMaster && (
              <button
                onClick={() => exportToExcel(allVitals, patientMap)}
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-teal-500 hover:bg-teal-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer shadow-sm"
              >
                <i className="ri-file-excel-2-line text-base"></i>
                Export to Excel
              </button>
            )}
          </div>
        </div>

        {successMsg && (
          <div className="mb-5 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-sm flex items-center gap-2">
            <i className="ri-checkbox-circle-line text-base"></i>
            {successMsg}
          </div>
        )}

        {/* Tabs */}
        {isAdminOrMaster && (
          <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'all' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <i className="ri-list-check-2 mr-1.5"></i>All Entries
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === 'search' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <i className="ri-search-line mr-1.5"></i>Search Patient
            </button>
            <button
              onClick={() => setActiveTab('share_requests')}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-colors cursor-pointer whitespace-nowrap relative ${activeTab === 'share_requests' ? 'bg-white text-teal-700 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <i className="ri-share-forward-line mr-1.5"></i>Share Requests
              {shareRequests.length > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                  {shareRequests.length}
                </span>
              )}
            </button>
          </div>
        )}

        {/* ── SHARE REQUESTS TAB ── */}
        {isAdminOrMaster && activeTab === 'share_requests' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                <i className="ri-share-forward-line text-teal-500"></i>
                WhatsApp Re-share Requests
              </h3>
              <span className="text-xs text-gray-400">{shareRequests.length} pending request{shareRequests.length !== 1 ? 's' : ''}</span>
            </div>
            {shareRequests.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-checkbox-circle-line text-5xl text-gray-200"></i>
                </div>
                <p className="text-sm font-medium text-gray-500">No pending requests</p>
                <p className="text-xs text-gray-400 mt-1">All re-share requests have been reviewed</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-50">
                {shareRequests.map((request) => (
                  <div key={request.id} className="px-6 py-4 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 className="text-sm font-semibold text-gray-800">{request.patientName}</h4>
                          <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">{request.patientId}</span>
                        </div>
                        <p className="text-xs text-gray-500 mb-2">
                          Requested by <strong>{request.requestedByName}</strong> on {formatDate(request.requestedAt)}
                        </p>
                        <div className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-50 text-yellow-700 text-xs font-medium rounded-full">
                          <i className="ri-time-line"></i>Pending Approval
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button onClick={() => handleApproveShareRequest(request)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                          <i className="ri-check-line"></i>Approve
                        </button>
                        <button onClick={() => handleRejectShareRequest(request)} className="inline-flex items-center gap-1 px-3 py-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer">
                          <i className="ri-close-line"></i>Reject
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ── ALL ENTRIES VIEW ── */}
        {isAdminOrMaster && activeTab === 'all' && (
          <>
            {dataLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="w-10 h-10 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                {/* Stats row */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                      <i className="ri-user-heart-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Patients</p>
                      <p className="text-xl font-bold text-gray-800">{allPatients.length}</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-indigo-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                      <i className="ri-file-list-3-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Total Vitals</p>
                      <p className="text-xl font-bold text-gray-800">{allVitals.length}</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                      <i className="ri-heart-pulse-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">BP Readings</p>
                      <p className="text-xl font-bold text-gray-800">{allVitalCounts['bp'] || 0}</p>
                    </div>
                  </div>
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex items-center gap-3">
                    <div className="w-10 h-10 bg-rose-500 rounded-full flex items-center justify-center text-white flex-shrink-0">
                      <i className="ri-drop-line text-lg"></i>
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">RBS Readings</p>
                      <p className="text-xl font-bold text-gray-800">{allVitalCounts['rbs'] || 0}</p>
                    </div>
                  </div>
                </div>

                {/* Search + filter bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 mb-5">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="relative flex-1">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <i className="ri-search-line text-gray-400 text-sm"></i>
                      </div>
                      <input
                        type="text"
                        value={allSearch}
                        onChange={(e) => setAllSearch(e.target.value)}
                        placeholder="Filter by patient name, ID, or contact..."
                        className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                      />
                    </div>
                    <select
                      value={allFilterType}
                      onChange={(e) => setAllFilterType(e.target.value)}
                      className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none bg-white cursor-pointer"
                    >
                      <option value="all">All Vital Types</option>
                      {vitalTypes.slice(1).map((t) => (
                        <option key={t} value={t}>{getVitalLabel(t)}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-3">
                    {vitalTypes.map((type) => {
                      const count = type === 'all' ? allVitals.length : allVitalCounts[type] || 0;
                      if (type !== 'all' && count === 0) return null;
                      return (
                        <button
                          key={type}
                          onClick={() => setAllFilterType(type)}
                          className={`px-3 py-1 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${allFilterType === type ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                        >
                          {type === 'all' ? 'All' : getVitalLabel(type)} ({count})
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Patient list */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                    <h3 className="text-base font-bold text-gray-800 flex items-center gap-2">
                      <i className="ri-user-heart-line text-teal-500"></i>Patients
                    </h3>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400">{filteredPatients.length} patient{filteredPatients.length !== 1 ? 's' : ''}</span>
                      <button onClick={loadAllData} disabled={dataLoading} className="w-8 h-8 flex items-center justify-center rounded-lg bg-teal-50 hover:bg-teal-100 text-teal-600 transition-colors cursor-pointer disabled:opacity-50" title="Refresh">
                        <i className={`ri-refresh-line text-sm ${dataLoading ? 'animate-spin' : ''}`}></i>
                      </button>
                    </div>
                  </div>
                  {filteredPatients.length === 0 ? (
                    <div className="text-center py-16">
                      <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-user-search-line text-5xl text-gray-200"></i>
                      </div>
                      <p className="text-sm font-medium text-gray-500">No patients found</p>
                      <p className="text-xs text-gray-400 mt-1">Try adjusting your search</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {filteredPatients.map((patient) => {
                        const pVitals = vitalsByPatient[patient.id] || [];
                        const typesRecorded = [...new Set(pVitals.map((v) => v.type))];
                        const lastVital = pVitals[0];
                        return (
                          <div key={patient.id} className="px-6 py-4 hover:bg-teal-50/40 transition-colors">
                            <div className="flex items-center gap-4">
                              <div className="w-11 h-11 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-base flex-shrink-0">
                                {patient.name.charAt(0).toUpperCase()}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-sm font-semibold text-gray-800">{patient.name}</span>
                                  <span className="text-xs text-gray-400 font-mono bg-gray-100 px-2 py-0.5 rounded">{patient.id}</span>
                                  <span className="px-2 py-0.5 bg-teal-50 text-teal-700 text-xs font-medium rounded-full border border-teal-100">{patient.bloodGroup}</span>
                                  {patient.whatsappShared && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-50 text-green-700 text-xs font-medium rounded-full border border-green-200">
                                      <i className="ri-check-line text-xs"></i>Shared ✓
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs text-gray-500 mt-0.5 truncate">{patient.college} &bull; {patient.department} &bull; {patient.contactNumber}</p>
                                {typesRecorded.length > 0 && (
                                  <div className="flex flex-wrap gap-1 mt-2">
                                    {typesRecorded.map((type) => (
                                      <span key={type} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getVitalBg(type)} ${getVitalColor(type)}`}>
                                        <i className={`${getVitalIcon(type)} text-xs`}></i>{getVitalLabel(type)}
                                      </span>
                                    ))}
                                  </div>
                                )}
                              </div>
                              <div className="flex-shrink-0 flex flex-col items-end gap-2">
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleWhatsAppShare(patient, pVitals)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                                  >
                                    <i className="ri-whatsapp-line text-sm"></i>
                                    {patient.whatsappShared ? 'Share Again' : 'Share'}
                                  </button>
                                  <button
                                    onClick={() => setExpandedPatientId(patient.id)}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 hover:bg-teal-100 text-teal-700 text-xs font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer border border-teal-100"
                                  >
                                    <i className="ri-eye-line text-sm"></i>View
                                  </button>
                                </div>
                                <div className="text-right">
                                  <p className="text-sm font-bold text-gray-700">{pVitals.length} vitals</p>
                                  {lastVital && <p className="text-xs text-gray-400">Last: {formatDate(lastVital.recordedAt)}</p>}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {/* ── SEARCH VIEW ── */}
        {(activeTab === 'search' || !isAdminOrMaster) && (
          <>
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Search Patient</label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <i className="ri-search-line text-gray-400"></i>
                </div>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search by Patient ID, Name, or Contact Number"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm"
                />
              </div>

              {showResults && searchResults.length > 0 && (
                <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto z-10 relative">
                  {searchResults.map((patient) => (
                    <button
                      key={patient.id}
                      onClick={() => selectPatient(patient)}
                      className="w-full px-4 py-3 hover:bg-teal-50 border-b border-gray-100 last:border-b-0 text-left cursor-pointer transition-colors"
                    >
                      <p className="text-sm font-semibold text-gray-800">{patient.name}</p>
                      <p className="text-xs text-gray-500">{patient.id} &bull; {patient.contactNumber} &bull; {patient.college}, {patient.department}</p>
                    </button>
                  ))}
                </div>
              )}

              {showResults && searchResults.length === 0 && searchTerm.trim().length > 0 && (
                <div className="mt-2 bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3">
                  <p className="text-sm text-gray-500">No patients found matching your search.</p>
                </div>
              )}

              {dataLoading && (
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-400">
                  <div className="w-4 h-4 border-2 border-teal-400 border-t-transparent rounded-full animate-spin"></div>
                  Loading patient data...
                </div>
              )}
            </div>

            {selectedPatient && (
              <>
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 bg-teal-500 rounded-full flex items-center justify-center text-white font-bold text-lg">
                      {selectedPatient.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <h2 className="text-lg font-bold text-gray-800">{selectedPatient.name}</h2>
                      <p className="text-sm text-gray-500">ID: {selectedPatient.id}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedPatient.whatsappShared && !isAdminOrMaster ? (
                        <>
                          <button
                            onClick={() => handleWhatsAppShare(selectedPatient, patientVitals)}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-400 text-white text-sm font-medium rounded-lg cursor-not-allowed shadow-sm"
                            title="Already shared - Request re-share"
                          >
                            <i className="ri-check-line text-base"></i>Shared ✓
                          </button>
                          <button
                            onClick={() => { setRequestPatientId(selectedPatient.id); setShowRequestModal(true); }}
                            className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-500 hover:bg-amber-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer shadow-sm"
                          >
                            <i className="ri-refresh-line text-base"></i>Request Re-share
                          </button>
                        </>
                      ) : (
                        <button
                          onClick={() => handleWhatsAppShare(selectedPatient, patientVitals)}
                          className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer shadow-sm"
                        >
                          <i className="ri-whatsapp-line text-base"></i>
                          {selectedPatient.whatsappShared && isAdminOrMaster ? 'Share Again' : 'Share via WhatsApp'}
                        </button>
                      )}
                      <span className="px-3 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-full border border-teal-200">{selectedPatient.bloodGroup}</span>
                      <span className="px-3 py-1 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">{selectedPatient.gender}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
                    <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">Age</p><p className="font-semibold text-gray-800">{selectedPatient.age} years</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">Contact</p><p className="font-semibold text-gray-800">{selectedPatient.contactNumber}</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">College</p><p className="font-semibold text-gray-800 truncate">{selectedPatient.college}</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">Department</p><p className="font-semibold text-gray-800 truncate">{selectedPatient.department}</p></div>
                    <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">Year / Designation</p><p className="font-semibold text-gray-800">{selectedPatient.yearDesignation}</p></div>
                    {selectedPatient.email && <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">Email</p><p className="font-semibold text-gray-800 truncate">{selectedPatient.email}</p></div>}
                    {selectedPatient.whatsappNumber && <div className="bg-gray-50 rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">WhatsApp</p><p className="font-semibold text-gray-800">{selectedPatient.whatsappNumber}</p></div>}
                    {selectedPatient.address && <div className="bg-gray-50 rounded-lg p-3 sm:col-span-2"><p className="text-xs text-gray-500 mb-1">Address</p><p className="font-semibold text-gray-800">{selectedPatient.address}</p></div>}
                  </div>

                  {selectedPatientBMI && (
                    <div className="mt-4 bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-lg p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-gray-600 mb-1">Body Mass Index (BMI)</p>
                          <p className={`text-2xl font-bold ${selectedPatientBMI.color}`}>{selectedPatientBMI.bmi}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500 mb-1">Category</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-sm font-semibold ${selectedPatientBMI.color} bg-white border-2`}>{selectedPatientBMI.category}</span>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t border-indigo-200">
                        <div className="flex justify-between text-xs text-gray-500">
                          <span>&lt;18.5 Underweight</span>
                          <span>18.5-24.9 Normal</span>
                          <span>25-29.9 Overweight</span>
                          <span>≥30 Obese</span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {patientVitals.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {vitalTypes.map((type) => {
                      const count = type === 'all' ? patientVitals.length : vitalCounts[type] || 0;
                      if (type !== 'all' && count === 0) return null;
                      return (
                        <button
                          key={type}
                          onClick={() => setFilterType(type)}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${filterType === type ? 'bg-teal-500 text-white' : 'bg-white border border-gray-200 text-gray-600 hover:bg-gray-50'}`}
                        >
                          {type === 'all' ? 'All' : getVitalLabel(type)} ({count})
                        </button>
                      );
                    })}
                  </div>
                )}

                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h3 className="text-base font-bold text-gray-800 mb-5 flex items-center gap-2">
                    <i className="ri-file-list-3-line text-teal-500"></i>
                    Vital Signs History
                    <span className="ml-auto text-xs font-normal text-gray-400">{filteredVitals.length} record{filteredVitals.length !== 1 ? 's' : ''}</span>
                  </h3>
                  {filteredVitals.length === 0 ? (
                    <div className="text-center py-16 bg-gray-50 rounded-xl">
                      <div className="w-14 h-14 flex items-center justify-center mx-auto mb-3">
                        <i className="ri-file-list-3-line text-5xl text-gray-200"></i>
                      </div>
                      <p className="text-sm font-medium text-gray-500">No vitals recorded yet</p>
                      <p className="text-xs text-gray-400 mt-1">Vitals will appear here once recorded</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {filteredVitals.map((vital) => (
                        <div key={vital.id} className="border border-gray-100 rounded-xl p-4 hover:shadow-sm transition-shadow bg-gray-50/50">
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex items-start gap-3 flex-1 min-w-0">
                              <div className={`w-10 h-10 ${getVitalBg(vital.type)} rounded-xl flex items-center justify-center flex-shrink-0`}>
                                <i className={`${getVitalIcon(vital.type)} text-lg ${getVitalColor(vital.type)}`}></i>
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <h4 className="text-sm font-semibold text-gray-800">{getVitalLabel(vital.type)}</h4>
                                  {vital.edited && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">
                                      <i className="ri-edit-line text-xs"></i>Edited
                                    </span>
                                  )}
                                </div>
                                <p className={`text-xl font-bold mt-0.5 ${getVitalColor(vital.type)}`}>
                                  {vital.value}{' '}
                                  <span className="text-sm font-normal text-gray-500">{vital.unit}</span>
                                </p>
                                {vital.edited && vital.previousValue && (
                                  <p className="text-xs text-amber-600 mt-1">
                                    Previous: {vital.previousValue} {vital.unit}
                                    {vital.editedAt && ` · Edited on ${formatDate(vital.editedAt)}`}
                                  </p>
                                )}
                                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-gray-400">
                                  <span className="flex items-center gap-1"><i className="ri-user-line"></i>{vital.recordedBy}</span>
                                  <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{formatDate(vital.recordedAt)}</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {vital.editRequested && (
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-yellow-100 text-yellow-700 text-xs font-medium rounded-full whitespace-nowrap">
                                  <i className="ri-time-line"></i>Edit Pending
                                </span>
                              )}
                              {canRequestEdit(vital) && (
                                <button
                                  onClick={() => openEditRequest(vital)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-teal-700 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                                >
                                  <i className="ri-edit-box-line"></i>Request Edit
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
      </div>

      {/* Patient Vitals Detail Modal */}
      {expandedPatient && (
        <PatientVitalsDetail
          patient={expandedPatient}
          patientVitals={expandedPatientVitals}
          onClose={() => setExpandedPatientId(null)}
          onEditRequest={openEditRequest}
          canRequestEdit={canRequestEdit}
          onShareWhatsApp={() => handleWhatsAppShare(expandedPatient, expandedPatientVitals)}
        />
      )}

      {/* Re-share Request Modal */}
      {showRequestModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-1">Request Re-share Permission</h3>
            <p className="text-sm text-gray-500 mb-5">This report has already been shared. Submit a request to share it again.</p>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-5 flex items-start gap-2">
              <i className="ri-information-line text-amber-600 text-lg flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-amber-700">An admin will review your request and approve or reject it.</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Re-share <span className="text-red-500">*</span></label>
              <textarea
                value={requestReason}
                onChange={(e) => { if (e.target.value.length <= 500) setRequestReason(e.target.value); }}
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm resize-none"
                placeholder="Explain why you need to share this report again"
                maxLength={500}
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{requestReason.length}/500</p>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleRequestReShare} disabled={!requestReason.trim()} className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">Submit Request</button>
              <button onClick={() => { setShowRequestModal(false); setRequestReason(''); setRequestPatientId(null); }} className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Request Modal */}
      {editModal.show && editModal.vital && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-1">Request Edit</h3>
            <p className="text-sm text-gray-500 mb-5">Submit a request to edit this vital record. An admin will review it.</p>
            <div className={`${getVitalBg(editModal.vital.type)} rounded-lg p-3 mb-5 flex items-center gap-3`}>
              <div className="w-9 h-9 bg-white rounded-lg flex items-center justify-center shadow-sm">
                <i className={`${getVitalIcon(editModal.vital.type)} text-sm ${getVitalColor(editModal.vital.type)}`}></i>
              </div>
              <div>
                <p className="text-sm font-semibold text-gray-800">{getVitalLabel(editModal.vital.type)}</p>
                <p className="text-xs text-gray-600">Current: <strong>{editModal.vital.value} {editModal.vital.unit}</strong></p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">New Value <span className="text-red-500">*</span></label>
                <input type="text" value={editNewValue} onChange={(e) => setEditNewValue(e.target.value)} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm" placeholder="Enter corrected value" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Edit <span className="text-red-500">*</span></label>
                <textarea value={editReason} onChange={(e) => { if (e.target.value.length <= 500) setEditReason(e.target.value); }} rows={3} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none text-sm resize-none" placeholder="Explain why this value needs to be changed" maxLength={500} />
                <p className="text-xs text-gray-400 mt-1 text-right">{editReason.length}/500</p>
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button onClick={handleSubmitEditRequest} disabled={!editReason.trim() || !editNewValue.trim()} className="flex-1 bg-teal-500 hover:bg-teal-600 text-white font-medium py-3 rounded-lg transition-colors whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed">Submit Request</button>
              <button onClick={() => { setEditModal({ vital: null, show: false }); setEditReason(''); setEditNewValue(''); }} className="flex-1 border border-gray-300 text-gray-700 font-medium py-3 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
